import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, ShoppingBag } from "lucide-react";
import { WhatsAppIcon } from "./Brand";
import { catLabel, formatPrice, waLink } from "../lib/site";

export default function ProductCard({ p, onEnquire, onBuy, index = 0 }) {
  const price = formatPrice(p.price);
  const canBuy = !!price && p.in_stock && !!onBuy;
  const isIbc = p.category === "ibc" || /\bIBC\b/i.test(`${p.name} ${p.pack_size}`);
  return (
    <motion.article
      data-testid={`product-card-${p.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col overflow-hidden rounded-[22px] border border-navy/10 bg-white transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(11,25,44,0.45)]"
    >
      <div className="relative aspect-[4/3.4] overflow-hidden spotlight">
        <img
          src={p.image || "/brand/mhm-logo.jpg"}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-contain p-5 mix-blend-multiply transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
        />
        <span className="absolute left-4 top-4 rounded-full bg-navy px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-widest text-white">
          {catLabel(p.category)}
        </span>
        {!p.in_stock && (
          <span className="absolute right-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-semibold text-red-600">Out of stock</span>
        )}
        <span className="absolute bottom-0 left-0 h-1 w-0 bg-brand transition-[width] duration-700 group-hover:w-full" />
      </div>
      <div className="flex flex-1 flex-col p-6">
        {p.pack_size && <p className="font-mono text-xs uppercase tracking-widest text-slate-500">{p.pack_size}</p>}
        <h3 className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-navy">{p.name}</h3>
        {p.description && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{p.description}</p>}
        <div className="mt-auto pt-6">
          <p data-testid={`product-price-${p.id}`} className="font-display text-3xl font-extrabold text-navy">
            {price ? (
              <>
                {price} <span className="font-body text-sm font-medium text-slate-500">{p.unit}</span>
              </>
            ) : (
              <span className="text-xl text-brand">Price on request</span>
            )}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {canBuy && (
              <button
                onClick={() => onBuy(p)}
                data-testid={`product-buy-${p.id}`}
                className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-brand-dark hover:-translate-y-0.5"
              >
                <ShoppingBag className="h-4 w-4" /> Buy now
              </button>
            )}
            {isIbc && (
              <Link
                to={`/bulk-orders?ibc=${encodeURIComponent(p.name)}#ibc-quote`}
                data-testid={`product-quote-${p.id}`}
                className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-brand-dark hover:-translate-y-0.5"
              >
                <FileText className="h-4 w-4" /> Request bulk quote
              </Link>
            )}
            <button
              onClick={() => onEnquire?.(p)}
              data-testid={`product-enquire-${p.id}`}
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-navy px-3 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand"
            >
              {price ? "Enquire" : "Get best price"} <ArrowUpRight className="h-4 w-4" />
            </button>
            <a
              href={waLink(`Hello MHM SUPER ADBLUE, I am interested in ${p.name}${p.pack_size ? ` (${p.pack_size})` : ""}. Please share price and availability.`)}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-whatsapp-${p.id}`}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-navy/15 px-4 py-3 text-sm font-semibold text-navy transition-colors hover:border-[#25D366] hover:text-[#128C4B]"
            >
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
